import type { Empresa } from "@/componentes/empresa/tipos"
import type { Persona } from "@/componentes/personas/persona"
import { comoFecha } from "@/lib/fechas"
import { componerTelefono, PAIS_POR_DEFECTO } from "@/lib/paises"

/**
 * Lo que el carnet dice además del nombre, la foto y el cargo: las filas de datos, la
 * web del QR y las normas del reverso.
 *
 * ⚠ **Vive aparte de las caras para poder probarlo sin pintar nada**, y porque es lo
 * único del carnet que decide QUÉ se escribe; las caras sólo deciden dónde.
 */

export type Fila = {
  etiqueta: string
  valor: string
  /** Se escribe más grande y en vino: lo que hay que ver primero. */
  realce?: boolean
}

/** La cédula tal como se imprime. */
export function cedulaDe(persona: Persona): string {
  return `${persona.tipo_documento}-${persona.documento}`
}

/** El teléfono de la agencia, con su prefijo; vacío si «Empresa» no tiene. */
export function telefonoDeLaAgencia(empresa?: Empresa): string {
  if (!empresa) return ""
  return componerTelefono(empresa.pais_telefono || PAIS_POR_DEFECTO, empresa.telefono ?? "")
}

/**
 * Las filas de la cara: cédula, ingreso, correo y teléfono, las que haya.
 *
 * ⚠ **El correo y el teléfono son los CORPORATIVOS, nunca los particulares.** Son los
 * mismos que salen en la firma de correo, que ya va a clientes. El `telefono` y el
 * `email_personal` de la ficha son privados, y una tarjeta se pierde.
 *
 * ⚠ **Sin teléfono corporativo propio sale el de la agencia**, como en la firma: a
 * quien llame le contesta la centralita en vez de nadie. Y una fila vacía no se
 * pinta: ni un guion ni un hueco.
 */
export function filasDeLaCara(persona: Persona, empresa?: Empresa): Fila[] {
  const propio = (persona.telefono_corporativo ?? "").trim()
  const telefono = propio
    ? componerTelefono(persona.pais_telefono || PAIS_POR_DEFECTO, propio)
    : telefonoDeLaAgencia(empresa)
  const filas: Fila[] = [
    { etiqueta: "Cédula", valor: cedulaDe(persona) },
    { etiqueta: "Ingreso", valor: comoFecha(persona.fecha_ingreso) },
    { etiqueta: "Correo", valor: (persona.email_corporativo ?? "").trim() },
    { etiqueta: "Teléfono", valor: telefono },
  ]
  return filas.filter((f) => f.valor !== "")
}

/**
 * La web de la agencia, lista para el QR; vacía si «Empresa» no tiene.
 *
 * ⚠ **Sale de «Empresa» y NO está escrita aquí.** Es la misma web que dice el resto del
 * sistema; escrita en el carnet sería un segundo sitio donde cambiarla, y el día que la
 * agencia estrene dominio los carnets impresos después seguirían mandando al viejo.
 *
 * ⚠ **Sin esquema se le pone `https://`**: con «setieca.com» a secas, algunos lectores
 * lo toman por texto y no ofrecen abrirlo. Y sólo `http` o `https`, que es lo único que
 * un teléfono abre como página.
 */
export function urlDeLaWeb(empresa?: Empresa): string {
  const web = (empresa?.sitio_web ?? "").trim()
  if (web === "") return ""
  const completa = /^[a-z][a-z0-9+.-]*:/i.test(web) ? web : `https://${web}`
  return /^https?:\/\/[^\s]+$/i.test(completa) ? completa : ""
}

/**
 * Las normas del reverso.
 *
 * ⚠ **Son de la agencia, no de la persona, y viven aquí: en un solo sitio.** Son las
 * de cualquier credencial corporativa; si la agencia quiere otras, se cambian aquí y
 * salen en el siguiente carnet. No van en «Empresa» porque no cambian lo bastante
 * como para pagar una columna, una migración y un formulario.
 */
export const NORMAS = [
  "Es personal e intransferible.",
  "Se lleva a la vista en la agencia y en todo recinto aduanero.",
  "Es propiedad de la empresa y se devuelve al terminar la relación laboral.",
] as const

/**
 * Las normas, más la última: a quién devolverlo.
 *
 * ⚠ **El teléfono va DENTRO de la norma y no en un bloque de contactos.** La versión
 * anterior llevaba teléfono, correo, web y dirección con sus iconos, y era la mitad de
 * lo que hacía el reverso tan cargado. Para devolver una tarjeta basta un número.
 */
export function normasDe(empresa?: Empresa): string[] {
  // ⚠ Con espacios duros: el número no se parte. Con los normales salía «… 823 35» en
  // una línea y «60.» en la siguiente, y un teléfono partido se marca mal.
  const telefono = telefonoDeLaAgencia(empresa).replace(/ /g, "\u00a0")
  return [
    ...NORMAS,
    telefono
      ? `Si lo encuentra, llame a la agencia: ${telefono}.`
      : "Si lo encuentra, devuélvalo a la agencia.",
  ]
}
